import dayjs from "dayjs";
import { PublicationShortname } from "src/app/auth/serviceIdentifier";

import { TVisitorContext } from "./viewerContext";

const EP_GA_CLICK_CATEGORY: Record<PublicationShortname, string> = {
  [PublicationShortname.BH]: "bh",
  [PublicationShortname.BT]: "bt",
  [PublicationShortname.SM]: "sm",
  [PublicationShortname.ST]: "st",
  [PublicationShortname.TM]: "tm",
  [PublicationShortname.ZB]: "zb",
  [PublicationShortname.BM]: "bm",
  [PublicationShortname.MM]: "mm",
  [PublicationShortname.TABLA]: "tabla",
  [PublicationShortname.DEFAULT]: "",
  [PublicationShortname.LRD]: "",
  [PublicationShortname.IN]: "",
  [PublicationShortname.GENG]: "",
  [PublicationShortname.HW]: "",
  [PublicationShortname.NY]: "",
  [PublicationShortname.HB]: ""
};

export enum GAClickAction {
  CLICK = "click",
  LOGIN = "login",
  CLOSE = "close",
}

export const GTM_ID = "GTM-KSTH25ZC";

export enum EP_GA_EVENT_NAME {
  PAGE_VIEW = "pages_page_view",
  CUSTOM = "custom_epaper_event",
}

/**
 * Preview (IRISX-45 / EPPR2-3146) tags its GA events with this cd_version
 * custom dimension so the data team can segment preview traffic. Non-preview
 * events send no cd_version today. Value confirmed against the GA Tagging Plan
 * sheet's dimension-definition row ("epaper" / "epaper preview").
 */
export const PREVIEW_CD_VERSION = "epaper preview";

// this constant used for chapter1 and pageName fields in GA event
export const PAGE_NAME = {
  ONBOARDING: "onboarding",
  PORTAL: "portal",
  SUBSCRIBE: "subscribe",
  LANDING: "landing",
};

/**
 *
 * @param date Date to be parsed
 * @returns {string} Google Analytics date format in string
 */
export function getTrackerDateString(date: Date): string {
  return dayjs(date).format("YYYY-MM-DD");
}

/**
 * get the Google Analytics authentication status
 * @param visitorCategory vistiorCategory from the visitorContext
 * @returns status whether the visitor is authenticated or not
 */
export function getGAAuthenticationStatus(
  visitorCategory: TVisitorContext["visitorCategory"],
): "success" | "failed" {
  return visitorCategory === "Anonymous" ? "failed" : "success";
}

/**
 *
 * @param pubId
 * @returns
 */
export function getGAPublicationName(pubId: PublicationShortname) {
  return EP_GA_CLICK_CATEGORY[pubId];
}

// GA event details are mixed primitives (e.g. numeric `pagination`), not all strings.
export type GAEventDetail = Record<string, string | number | undefined>;

/**
 * customized Google Analytics hooks
 */
export function useEpGTMDispatch(): ({
  eventName,
  eventDetail,
}: {
  eventName: string;
  eventDetail: GAEventDetail;
}) => void {
  const customDispatch = ({
    eventName,
    eventDetail,
  }: {
    eventName: string;
    eventDetail: GAEventDetail;
  }) => {
    if (typeof window === "undefined") return;
    const w = window as unknown as Window & { dataLayer: Record<string, unknown>[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: "gaEvent",
      event_name: eventName,
      event_details: eventDetail,
    });
  };

  return customDispatch;
}
